/**
 * `checkUserPermission` — handler-side user RBAC for the
 * `UserActorEnvelope` v2 envelope (D308 + request 6c79d20d).
 *
 * The v2 envelope carries only a `permissions_digest` (sha256 of the
 * user's effective permission set), NOT the permission list itself. This
 * keeps the `x-user-context` header small and lets the handler detect a
 * STALE user JWT: the handler resolves `user -> roles -> permissions`
 * against its OWN replicas via a globally-configured resolver, recomputes
 * the digest, and compares it to the digest the JWT carried. A mismatch
 * means the grant set changed after the JWT was minted — the handler
 * MUST reject and force a re-issue rather than trust a stale digest.
 *
 * D308 (request f4d47a07): `checkUserPermission` is a PURE FUNCTIONAL
 * API over an ALREADY-VERIFIED `UserActorEnvelope` — JWT verification is
 * done UPSTREAM by the auth middleware. It therefore MUST NOT require the
 * JWT-verify config (`expectedIssuer` / `expectedAudience` /
 * `jwksResolver`). The `userPermissionResolver` can be supplied two ways:
 *   1. INLINE via `opts.resolver` — works with NO `configureGrpcAuth`
 *      call at all (per-service-auth consumers that use `withAuthUnary`
 *      AUTH_OPTS rather than a global resolver). The inline resolver WINS.
 *   2. GLOBALLY via `configureGrpcAuth({ userPermissionResolver })` — the
 *      original path, used only when no inline resolver is passed. This is
 *      read via the NON-throwing `getGrpcAuthConfigOrNull()` peek, so an
 *      inline resolver never trips the throwing `getGrpcAuthConfig()`.
 *
 * R1 (NON-NEGOTIABLE): there is NO Noop / no-op default resolver. If
 * neither an inline `opts.resolver` nor a globally-configured
 * `userPermissionResolver` is present, this throws
 * `CheckUserPermissionNotConfigured`. The in-memory TTL cache below is a
 * REAL cache (module-global `Map`), not a stub.
 *
 * Digest contract (D308 canonical — MUST be byte-identical across TS /
 * Python / Go). The digest is:
 *
 *   sha256_hex( JSON.stringify( perms.sort() ) ).slice(0, 32)
 *
 * where `JSON.stringify` is COMPACT (no spaces) over the
 * lexicographically-sorted permission strings, and only the FIRST 32 hex
 * chars are kept. Python MUST pass `separators=(",", ":")` and Go relies
 * on `json.Marshal` already being compact. Byte-parity anchor: the perms
 * `["audit:chain:read","audit:chain:acknowledge"]` hash to exactly
 * `71cb5ce51e6479ef13a1b91cb16a5dbe`.
 */
import { createHash } from "node:crypto";
import { getGrpcAuthConfigOrNull } from "../configure";
import { processGlobalSlot } from "../global-state";
import type { UserActorEnvelope } from "../types/user-actor";

/**
 * Resolver the consumer registers at bootstrap. Returns the user's
 * EFFECTIVE permission keys in the given tenant (the consumer resolves
 * `user -> roles -> permissions` against its own RBAC replicas). Mirrors
 * `@nodii/approval`'s `MembershipRolesLookup`
 * (`ts/approval/src/types.ts:230`).
 */
export type UserPermissionResolver = (
  userId: string,
  tenantId: string,
) => Promise<readonly string[]>;

/** Options for {@link checkUserPermission}. */
export type CheckUserPermissionOptions = {
  /**
   * TTL (seconds) for a resolved permission set in the in-memory cache.
   * Defaults to 30. Because `permissions_digest` is part of the cache
   * key, a permission change (new digest) is automatically a cache miss
   * regardless of TTL.
   */
  cacheTtlSec?: number;
  /**
   * INLINE resolver (D308 / request f4d47a07). Same type as the global
   * `configureGrpcAuth({ userPermissionResolver })`:
   * `(userId, tenantId) => Promise<readonly string[]>` returning the
   * user's effective permissions. When supplied, this WINS over the
   * global resolver and `checkUserPermission` runs WITHOUT any
   * `configureGrpcAuth` call — keeping it a pure-functional API over an
   * already-verified envelope (no JWT-verify config required).
   */
  resolver?: UserPermissionResolver;
};

/** Result of a {@link checkUserPermission} call. */
export type CheckUserPermissionResult = {
  /** `true` iff `permissionKey` is in the resolved permission set. */
  granted: boolean;
  /** The full effective permission set resolved for the user/tenant. */
  resolvedPermissions: string[];
  /** When the resolved set was produced (cache-entry creation time). */
  asOf: Date;
};

/**
 * Thrown when `checkUserPermission` is called but no
 * `userPermissionResolver` was supplied by EITHER path: inline via
 * `opts.resolver`, or globally via
 * `configureGrpcAuth({ userPermissionResolver })`. R1: there is no
 * default resolver — the consumer is the injection point for its own
 * RBAC store.
 */
export class CheckUserPermissionNotConfigured extends Error {
  constructor() {
    super(
      "checkUserPermission: no userPermissionResolver supplied — pass it INLINE via opts.resolver (pure-functional, no configureGrpcAuth needed) OR globally via configureGrpcAuth({ userPermissionResolver }), so the handler can resolve user->roles->permissions against its own replicas (R1: there is no default resolver)",
    );
    this.name = "CheckUserPermissionNotConfigured";
  }
}

/**
 * Thrown when the actor envelope is malformed (missing `user_id` or
 * `permissions_digest`).
 */
export class CheckUserPermissionValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CheckUserPermissionValidationError";
  }
}

/**
 * Thrown when the digest of the freshly-resolved permission set does NOT
 * match the digest the user JWT carried — the JWT is stale and the user
 * must re-authenticate / re-issue. Error `name` is the canonical D308
 * token `PERMISSION_DIGEST_MISMATCH`.
 */
export class PermissionDigestMismatch extends Error {
  /** Digest carried by the (stale) actor envelope / JWT. */
  readonly actorDigest: string;
  /** Digest recomputed from the freshly-resolved permission set. */
  readonly resolvedDigest: string;

  constructor(actorDigest: string, resolvedDigest: string) {
    super(
      `PERMISSION_DIGEST_MISMATCH: the user JWT is stale — its permissions_digest (${actorDigest}) does not match the freshly-resolved permission set digest (${resolvedDigest}). Re-issue the user token.`,
    );
    this.name = "PERMISSION_DIGEST_MISMATCH";
    this.actorDigest = actorDigest;
    this.resolvedDigest = resolvedDigest;
  }
}

/**
 * Compute the D308 canonical permissions digest: sha256 hex of the
 * COMPACT `JSON.stringify` of the lexicographically-sorted permission
 * list, truncated to the first 32 hex chars.
 *
 * Pure + deterministic. Byte-parity anchor:
 * `computePermissionsDigest(["audit:chain:read","audit:chain:acknowledge"])`
 * === `"71cb5ce51e6479ef13a1b91cb16a5dbe"`.
 */
export function computePermissionsDigest(
  permissions: readonly string[],
): string {
  const sorted = [...permissions].sort();
  return createHash("sha256")
    .update(JSON.stringify(sorted))
    .digest("hex")
    .slice(0, 32);
}

type CacheEntry = {
  permissions: string[];
  asOf: Date;
  expiresAt: number;
};

/**
 * Process-global in-memory TTL cache. Keyed by
 * `user_id:tenantId:permissions_digest` so a permission change (new digest)
 * is automatically a cache miss. This is a REAL cache (R1), not a stub.
 *
 * Held on a `globalThis` slot (NOT a module-level Map) so the tsup
 * `splitting: false` subpath bundles share ONE cache — otherwise each subpath
 * that calls `checkUserPermission` keeps its own duplicate cache (redundant
 * lookups; same subpath-split class as req 4f1facd4).
 */
const cacheSlot = processGlobalSlot<Map<string, CacheEntry>>("permissionCache");

function permissionCache(): Map<string, CacheEntry> {
  let m = cacheSlot.get();
  if (!m) {
    m = new Map<string, CacheEntry>();
    cacheSlot.set(m);
  }
  return m;
}

/**
 * Test-only reset hook. Wipes the process-global permission cache between
 * cases. NOT a production code path.
 */
export function resetCheckUserPermissionCacheForTesting(): void {
  permissionCache().clear();
}

/**
 * Resolve whether the user described by `actor` holds `permissionKey` in
 * `tenantId`.
 *
 * Flow:
 *   1. Validate the actor has a non-empty `user_id` + `permissions_digest`.
 *   2. Cache lookup by `user_id:tenantId:permissions_digest`. On a
 *      non-expired hit, reuse the cached `{ permissions, asOf }` WITHOUT
 *      calling the resolver again.
 *   3. On a miss, resolve the `userPermissionResolver` — INLINE
 *      `opts.resolver` WINS, else fall back to the global resolver via
 *      the NON-throwing `getGrpcAuthConfigOrNull()` peek (throwing
 *      `CheckUserPermissionNotConfigured` only if NEITHER is present),
 *      resolve the permissions, recompute the digest, and reject with
 *      `PermissionDigestMismatch` if it differs from the actor's digest
 *      (stale JWT). Store the result with `expiresAt = now + ttl`.
 *   4. `granted = permissions.includes(permissionKey)`.
 *
 * @param actor          the decoded `UserActorEnvelope` v2.
 * @param permissionKey  the permission to check for (e.g.
 *                       `"audit:chain:read"`).
 * @param tenantId       the tenant the check is scoped to.
 * @param opts           optional cache TTL override + inline resolver.
 */
export async function checkUserPermission(
  actor: UserActorEnvelope,
  permissionKey: string,
  tenantId: string,
  opts?: CheckUserPermissionOptions,
): Promise<CheckUserPermissionResult> {
  if (!actor || !actor.user_id) {
    throw new CheckUserPermissionValidationError(
      "checkUserPermission: actor.user_id is required",
    );
  }
  if (!actor.permissions_digest) {
    throw new CheckUserPermissionValidationError(
      "checkUserPermission: actor.permissions_digest is required",
    );
  }

  const cacheKey = `${actor.user_id}:${tenantId}:${actor.permissions_digest}`;
  const now = Date.now();

  const cached = permissionCache().get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return {
      granted: cached.permissions.includes(permissionKey),
      resolvedPermissions: cached.permissions,
      asOf: cached.asOf,
    };
  }

  // Inline resolver WINS (D308 pure-functional path); only peek the
  // global config when no inline resolver was passed. The peek is
  // NON-throwing, so an inline resolver never trips the throwing
  // getGrpcAuthConfig() — checkUserPermission runs with NO
  // configureGrpcAuth call at all.
  const resolver =
    opts?.resolver ?? getGrpcAuthConfigOrNull()?.userPermissionResolver;
  if (!resolver) {
    throw new CheckUserPermissionNotConfigured();
  }

  const resolved = await resolver(actor.user_id, tenantId);
  const permissions = [...resolved];

  const resolvedDigest = computePermissionsDigest(permissions);
  if (resolvedDigest !== actor.permissions_digest) {
    throw new PermissionDigestMismatch(
      actor.permissions_digest,
      resolvedDigest,
    );
  }

  const cacheTtlSec = opts?.cacheTtlSec ?? 30;
  const asOf = new Date(now);
  permissionCache().set(cacheKey, {
    permissions,
    asOf,
    expiresAt: now + cacheTtlSec * 1000,
  });

  return {
    granted: permissions.includes(permissionKey),
    resolvedPermissions: permissions,
    asOf,
  };
}
